import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useFirm } from "../context/FirmContext";

const NAV = [
  { to: "/", label: "Index" },
  { to: "/services", label: "Services" },
  { to: "/team", label: "Practice" },
  { to: "/insights", label: "Insights" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[var(--bone)]/85 backdrop-blur-xl border-b border-[var(--rule)]" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="header-logo" className="group">
          <div className="font-serif text-2xl tracking-tight leading-none">
            Attry <span className="italic font-light">&amp;</span> Associates
          </div>
          <div className="font-sans text-[10px] tracking-[0.32em] uppercase text-neutral-500 mt-1">
            Chartered Accountants · Est. 2017
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `editorial-link text-[11px] tracking-[0.28em] uppercase font-medium ${
                  isActive ? "active text-neutral-950" : "text-neutral-600 hover:text-neutral-950"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <button
          onClick={() => setOpen((o) => !o)}
          className="md:hidden p-2 -mr-2"
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[var(--rule)] bg-[var(--bone)]" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-serif text-3xl ${isActive ? "text-neutral-950" : "text-neutral-500"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

const Footer = () => {
  const firm = useFirm();
  return (
    <footer data-testid="site-footer" className="bg-neutral-950 text-neutral-50 mt-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-7">
            <p className="text-[11px] tracking-[0.32em] uppercase text-neutral-500 mb-8">
              — Speak with the practice
            </p>
            <a
              href={`mailto:${firm?.email || "anmol@attryassociates.in"}`}
              data-testid="footer-email"
              className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-none block hover:text-neutral-300 transition-colors break-all"
            >
              {firm?.email || "anmol@attryassociates.in"}
            </a>
            {firm?.phone && (
              <a
                href={`tel:${firm.phone.replace(/\s/g, "")}`}
                data-testid="footer-phone"
                className="font-serif text-3xl sm:text-4xl font-light tracking-tight mt-4 block hover:text-neutral-300 transition-colors"
              >
                {firm.phone}
              </a>
            )}
          </div>

          <div className="lg:col-span-5 grid grid-cols-2 gap-8 lg:pt-2">
            <div>
              <p className="text-[11px] tracking-[0.32em] uppercase text-neutral-500 mb-4">Office</p>
              <p className="text-sm text-neutral-300 leading-relaxed">
                {firm?.address || "Sector 17, Chandigarh"}
                <br />
                {firm?.city || "Chandigarh, India"}
              </p>
            </div>
            <div>
              <p className="text-[11px] tracking-[0.32em] uppercase text-neutral-500 mb-4">Practice</p>
              <ul className="text-sm text-neutral-300 space-y-2">
                <li>Litigation</li>
                <li>Consulting</li>
                <li>Assurance</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-20 pt-8 border-t border-neutral-800 flex flex-col md:flex-row justify-between gap-4 text-[11px] tracking-[0.24em] uppercase text-neutral-500">
          <span>© {new Date().getFullYear()} Attry &amp; Associates · All rights reserved</span>
          <span>Founded {firm?.established || "2017"} · {firm?.founder || "CA. Anmol Attry"}</span>
        </div>
      </div>
    </footer>
  );
};

const TITLE_BY_PATH = {
  "/": "Attry & Associates · Chartered Accountants",
  "/services": "Services · Attry & Associates",
  "/team": "Practice · Attry & Associates",
  "/insights": "Insights · Attry & Associates",
  "/contact": "Contact · Attry & Associates",
};
const DEFAULT_TITLE = "Attry & Associates · Chartered Accountants";

const Layout = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);

  // Force-set the document title and defend against dynamic platform overrides
  useEffect(() => {
    const desired =
      TITLE_BY_PATH[pathname] ||
      (pathname.startsWith("/insights/") ? "Insights · Attry & Associates" : DEFAULT_TITLE);

    const apply = () => { if (document.title !== desired) document.title = desired; };
    apply();
    const interval = setInterval(apply, 500);
    const observer = new MutationObserver(apply);
    const titleEl = document.querySelector("title");
    if (titleEl) observer.observe(titleEl, { childList: true });

    return () => { clearInterval(interval); observer.disconnect(); };
  }, [pathname]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-20" data-testid="page-main">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
